<template>
  <div>
    <Banner type="part" />
    <div class="page-container j-container-box-shadow container">
      <div class="title">
        <div class="j-title">{{ $t('catalogue.title') }}</div>
        <a-divider type="vertical" style="margin: 0 40px; height: 30px;" />
        <div class="j-content">
          <span class="model-label ">
            {{ $t('catalogue.model_label') }} :
          </span>
          <span>{{ model || '' }}</span>
        </div>
      </div>

      <a-divider style="margin: 30px 0;" />

      <a-row :gutter="[0, { xs: 24, sm: 24 }]" v-if="catalogues.length > 0">
        <a-col :xs="0" :sm="0" :md="6"> </a-col>

        <a-col :xs="24" :sm="24" :md="8">
          <a-form
            class="part-no-form"
            layout="inline"
            ref="numForm"
            :model="partNoQueryParam"
          >
            <a-form-item
              name="modelNum"
              :rules="{
                type: 'string',
                required: true,
                message: $t('part.search_by_part_no_msg')
              }"
            >
              <a-input-search
                v-model:value="partNoQueryParam.modelNum"
                :placeholder="$t('catalogue.search_by_part_no')"
                @search="onSearchNo"
                @change="onSearchModelNumChange"
              >
                <template #enterButton>
                  <a-button :style="getSearchBtn1Style()">
                    <template #icon>
                      <SearchIcon :isActive="!!partNoQueryParam.modelNum" />
                    </template>
                  </a-button>
                </template>
              </a-input-search>
            </a-form-item>
          </a-form>
        </a-col>

        <a-col :xs="0" :sm="0" :md="2" style="text-align: center;">
          <a-divider type="vertical" style="height: 32px;" />
        </a-col>

        <a-col :xs="24" :sm="24" :md="8">
          <a-form
            ref="nameForm"
            :model="partNameQueryParam"
            class="part-name-form"
            layout="inline"
          >
            <a-form-item
              name="name"
              :rules="{
                type: 'string',
                required: true,
                message: $t('part.search_by_part_name_msg')
              }"
            >
              <a-input-search
                v-model:value="partNameQueryParam.name"
                :placeholder="$t('catalogue.search_by_part_name')"
                @search="onSearchName"
                @change="onSearchNameChange"
              >
                <template #enterButton>
                  <a-button :style="getSearchBtn2Style()">
                    <template #icon>
                      <SearchIcon :isActive="!!partNameQueryParam.name" />
                    </template>
                  </a-button>
                </template>
              </a-input-search>
            </a-form-item>
          </a-form>
        </a-col>
      </a-row>

      <a-spin :spinning="loading">
        <div>
          <div class="catalog-container" v-if="catalogues.length > 0">
            <a-row :gutter="[45, 45]">
              <a-col :xs="24" :md="6" v-for="item in catalogues" :key="item.id">
                <CatalogueItem
                  :vehicleId="vehicleId"
                  :model="model"
                  :item="item"
                />
              </a-col>
            </a-row>
          </div>
          <div class="j-no-result" v-if="loaded && catalogues.length === 0">
            <img
              alt="no-data"
              src="@/assets/images/noData.png"
              width="164"
              height="141"
            />
            <span class="no-data-msg j-content">
              {{ $t('common.no_data') }}
            </span>
          </div>
        </div>
      </a-spin>

      <div class="pagination-container" v-if="catalogues.length > 0">
        <a-pagination
          v-model:current="pageNo"
          :page-size-options="pageSizeOptions"
          :total="total"
          show-size-changer
          v-model:pageSize="pageSize"
          @showSizeChange="onShowSizeChange"
          @change="onChange"
          show-quick-jumper
        >
          <template #buildOptionText="props">
            <span>{{ props.value }} {{ $t('common.page_per_item_text') }}</span>
          </template>
        </a-pagination>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { useRoute } from 'vue-router';
import Banner from '@/components/banner/Banner.vue';
import SearchIcon from '@/components/icons/SearchIcon.vue';
import { getPartList, getPartPartsList } from '@/api/apis';
import CatalogueItem from './CatalogueItem.vue';

export default defineComponent({
  name: 'Catalogue',
  components: {
    SearchIcon,
    CatalogueItem,
    Banner
  },
  data() {
    return {
      loading: false,
      loaded: false,
      catalogues: [],
      pageNo: 1,
      pageSize: 8,
      pageSizeOptions: ['8', '16'],
      current: 1,
      total: 0,
      partNameQueryParam: {
        name: ''
      },
      partNoQueryParam: {
        modelNum: ''
      },
      // params & query
      vehicleId: 0,
      model: ''
    };
  },
  created() {
    const route = useRoute();

    const params = route.params;

    const query = route.query;

    this.model = query.model as string;

    if (params.vehicleId) {
      this.vehicleId = Number(params.vehicleId);

      if (this.vehicleId > 0) {
        this.loadData();
      }
    }
  },
  methods: {
    onShowSizeChange() {
      this.pageNo = 1;
      this.loadData();
    },
    onChange() {
      this.loadData();
    },
    async loadData() {
      this.loading = true;

      const res = await getPartList({
        pageNo: this.pageNo,
        pageSize: this.pageSize,
        vehicleId: this.vehicleId,
        type: 1 // 图册
      });

      if (res.success) {
        const data = res.result || {};

        this.catalogues = data.records || [];
        this.total = data.total;
      }

      this.loading = false;

      this.loaded = true;
    },
    getSearchBtn1Style() {
      const isActive = !!this.partNoQueryParam.modelNum;
      return {
        width: 'auto',
        'background-color': !isActive ? '#E5EAF4' : '#039'
      };
    },
    getSearchBtn2Style() {
      const isActive = !!this.partNameQueryParam.name;
      return {
        width: 'auto',
        'background-color': !isActive ? '#E5EAF4' : '#039'
      };
    },
    onSearchNo() {
      const form = (this.$refs as any).numForm;

      form
        .validate()
        .then(async () => {
          const res = await getPartPartsList({
            modelNum: this.partNoQueryParam.modelNum
              ? this.partNoQueryParam.modelNum.trim()
              : '',
            vehicleId: this.vehicleId
          });
          if (res.success) {
            const data = res.result || {};
            const hasResult = data.total >= 2;
            if (data.total === 1) {
              window.open(
                `/partdetail/${data.records[0].id}?modelNum=${this.partNoQueryParam.modelNum}`
              );
            } else {
              window.open(
                `/part-result?isNo=1&modelNum=${
                  this.partNoQueryParam.modelNum
                }&vehicleId=${this.vehicleId}&hasResult=${Number(hasResult)}`
              );
            }
          }
        })
        .catch();
    },
    onSearchName() {
      const form = (this.$refs as any).nameForm;
      form
        .validate()
        .then(async () => {
          const res = await getPartPartsList({
            partName: this.partNameQueryParam.name
              ? this.partNameQueryParam.name.trim()
              : '',
            vehicleId: this.vehicleId
          });
          if (res.success) {
            const data = res.result || {};
            const hasResult = data.total >= 2;
            if (data.total === 1) {
              window.open(
                `/partdetail/${data.records[0].id}?name=${this.partNameQueryParam.name}`
              );
            } else {
              window.open(
                `/part-result?isNo=0&partName=${
                  this.partNameQueryParam.name
                }&vehicleId=${this.vehicleId}&hasResult=${Number(
                  hasResult
                )}&model=${this.model || ''}`
              );
            }
          }
        })
        .catch();
    },
    onSearchModelNumChange() {
      if (!this.partNoQueryParam.modelNum) {
        const form = (this.$refs as any).numForm;

        this.$nextTick(() => {
          form.clearValidate('modelNum');
        });
      }
    },
    onSearchNameChange() {
      if (!this.partNameQueryParam.name) {
        const form = (this.$refs as any).nameForm;

        this.$nextTick(() => {
          form.clearValidate('name');
        });
      }
    }
  }
});
</script>

<style lang="scss" scoped>
.container {
  padding: 0 49px;
  background-color: white;

  .title {
    padding: 32px 0 0 0;
    display: flex;
    align-items: center;
    color: $primary-color;
  }

  .sub-title {
    padding: 30px 0;
    display: block;
  }

  .catalog-container {
    padding-top: 30px;
  }

  .part-name-form,
  .part-no-form {
    display: flex;
    &::v-deep(.ant-form-item) {
      flex: 1;

      & .ant-form-item-control-wrapper {
        width: 100%;
      }

      & .ant-form-item-control {
        line-height: initial;
      }
    }

    &::v-deep(.ant-form-item:last-child) {
      margin-right: 0;
    }
  }

  .pagination-container {
    padding: 38px 0 40px 0;
    text-align: right;
  }
}
::v-deep(.ant-input-group-addon) {
  .ant-btn {
    padding: 0 15px;
  }
}
</style>
